import { readonly, shallowRef } from 'vue'

import type { MapUserLocation } from '@/types'

export function useUserLocation() {
  const location = shallowRef<MapUserLocation>()
  const isLocating = shallowRef(false)
  const error = shallowRef('')

  function locate() {
    error.value = ''

    if (!window.isSecureContext) {
      error.value = 'Location access requires HTTPS or localhost.'
      return
    }

    if (!navigator.geolocation) {
      error.value = 'This browser does not support location access.'
      return
    }

    isLocating.value = true
    navigator.geolocation.getCurrentPosition(
      (position) => {
        location.value = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }
        isLocating.value = false
      },
      (positionError) => {
        error.value = describePositionError(positionError)
        isLocating.value = false
      },
      {
        enableHighAccuracy: true,
        maximumAge: 30_000,
        timeout: 15_000,
      },
    )
  }

  return {
    location: readonly(location),
    isLocating: readonly(isLocating),
    error: readonly(error),
    locate,
  }
}

function describePositionError(error: GeolocationPositionError) {
  if (error.code === error.PERMISSION_DENIED) {
    return 'Location permission was denied. Allow it in your browser and try again.'
  }
  if (error.code === error.POSITION_UNAVAILABLE) {
    return 'Your current position is unavailable.'
  }
  if (error.code === error.TIMEOUT) {
    return 'Finding your position took too long. Try again.'
  }
  return 'Your current position could not be found.'
}
